import React, { useEffect, useMemo, useState } from "react";
import { Layout } from "@/components/Layout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { useT } from "@/i18n/I18nContext";
import { getSalesTrend, fmt } from "@/lib/api";
import { BarChart3, TrendingUp, TrendingDown, Minus, Package, Coins } from "lucide-react";
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
} from "recharts";

const CHART_COLORS = ["#0E7490", "#14B8A6", "#F59E0B", "#3B82F6", "#22C55E", "#A855F7"];
const TOOLTIP_STYLE = { fontSize: 12, borderRadius: 8, border: "1px solid hsl(var(--border))" };
const BAR_CURSOR = { fill: "hsl(var(--secondary))" };
const CHART_MARGIN = { left: -14, right: 8, top: 8 };
const LEGEND_STYLE = { fontSize: 12 };
const PERIODS = ["7d", "30d", "90d", "month", "year"];
const ALL = "__all__";

const Delta = ({ pct, current, testid }) => {
  const { t } = useT();
  if (pct === null || pct === undefined) {
    if (Number(current) > 0) {
      return <Badge className="badge-ok gap-1 text-[10px]" data-testid={testid}><TrendingUp className="h-3 w-3" /> {t("dashboard.newSales")}</Badge>;
    }
    return <span className="text-xs text-muted-foreground" data-testid={testid}>—</span>;
  }
  if (pct > 0) return <span className="inline-flex items-center gap-1 text-xs font-medium text-[hsl(var(--success))]" data-testid={testid}><TrendingUp className="h-3.5 w-3.5" /> +{pct}%</span>;
  if (pct < 0) return <span className="inline-flex items-center gap-1 text-xs font-medium text-[hsl(var(--danger))]" data-testid={testid}><TrendingDown className="h-3.5 w-3.5" /> {pct}%</span>;
  return <span className="inline-flex items-center gap-1 text-xs font-medium text-muted-foreground" data-testid={testid}><Minus className="h-3.5 w-3.5" /> 0%</span>;
};

export default function Reports() {
  const { t, td } = useT();
  const [period, setPeriod] = useState("30d");
  const [product, setProduct] = useState(ALL);
  const [data, setData] = useState(null);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    getSalesTrend(period, product === ALL ? "" : product)
      .then((d) => {
        if (!alive) return;
        setData(d);
        if (Array.isArray(d.products) && d.products.length) setProducts(d.products);
      })
      .catch(() => { if (alive) setData(null); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [period, product]);

  const periodLabel = {
    "7d": t("dashboard.period7d"), "30d": t("dashboard.period30d"), "90d": t("dashboard.period90d"),
    "month": t("dashboard.periodMonth"), "year": t("dashboard.periodYear"),
  };

  const units = data?.units || [];
  const currency = data?.currency || "RON";
  const rev = data?.revenue || { current: 0, previous: 0, change_pct: null };
  const totalQty = useMemo(() => (data?.by_unit || []).reduce((a, u) => a + (u.current || 0), 0), [data]);
  const hasData = data && (data.by_product || []).length > 0;

  return (
    <Layout>
      <div className="mb-5 flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <h1 className="flex items-center gap-2 text-2xl font-semibold tracking-tight">
            <BarChart3 className="h-6 w-6 text-[hsl(var(--brand))]" /> {t("reports.title")}
          </h1>
          <p className="mt-1 max-w-2xl text-sm text-muted-foreground">{t("reports.subtitle")}</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Select value={product} onValueChange={setProduct}>
            <SelectTrigger className="h-9 w-[190px] text-sm" data-testid="reports-product">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL} data-testid="reports-product-all">{t("dashboard.allProducts")}</SelectItem>
              {products.map((p) => <SelectItem key={p} value={p} data-testid={`reports-product-${p}`}>{td(p)}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={period} onValueChange={setPeriod}>
            <SelectTrigger className="h-9 w-[150px] text-sm" data-testid="reports-period">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {PERIODS.map((p) => <SelectItem key={p} value={p} data-testid={`reports-period-${p}`}>{periodLabel[p]}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
        <Card data-testid="reports-revenue-card">
          <CardContent className="p-5">
            <div className="flex items-center gap-1.5 text-xs text-muted-foreground"><Coins className="h-4 w-4" /> {t("reports.revenue")}</div>
            <div className="mt-2 flex items-baseline justify-between gap-2">
              <span className="font-mono text-2xl font-semibold tabular-nums">{fmt(rev.current, 2)} {currency}</span>
              <Delta pct={rev.change_pct} current={rev.current} testid="reports-revenue-delta" />
            </div>
            <div className="mt-1 text-xs text-muted-foreground">{t("dashboard.vsPrev")} {fmt(rev.previous, 2)} {currency}</div>
          </CardContent>
        </Card>
        <Card data-testid="reports-qty-card">
          <CardContent className="p-5">
            <div className="flex items-center gap-1.5 text-xs text-muted-foreground"><Package className="h-4 w-4" /> {t("reports.totalQty")}</div>
            <div className="mt-2 font-mono text-2xl font-semibold tabular-nums">{fmt(totalQty, 2)}</div>
            <div className="mt-1 text-xs text-muted-foreground">{t("reports.acrossUnits", { n: units.length })}</div>
          </CardContent>
        </Card>
        <Card data-testid="reports-lines-card">
          <CardContent className="p-5">
            <div className="flex items-center gap-1.5 text-xs text-muted-foreground"><BarChart3 className="h-4 w-4" /> {t("reports.productLines")}</div>
            <div className="mt-2 font-mono text-2xl font-semibold tabular-nums">{(data?.by_product || []).length}</div>
            <div className="mt-1 text-xs text-muted-foreground">{periodLabel[period]}</div>
          </CardContent>
        </Card>
      </div>

      {/* Trend chart */}
      <Card className="mt-4">
        <CardHeader><CardTitle className="text-base">{t("reports.trendTitle")}</CardTitle></CardHeader>
        <CardContent>
          {loading ? (
            <div className="h-[300px] animate-pulse rounded-lg bg-secondary" />
          ) : !hasData ? (
            <div className="flex h-[240px] flex-col items-center justify-center text-center" data-testid="reports-empty">
              <BarChart3 className="h-9 w-9 text-muted-foreground/40" />
              <p className="mt-3 text-sm text-muted-foreground">{t("dashboard.noSales")}</p>
            </div>
          ) : (
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={data.series} margin={CHART_MARGIN}>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
                <XAxis dataKey="label" fontSize={11} tickLine={false} axisLine={false} stroke="hsl(var(--muted-foreground))" interval="preserveStartEnd" minTickGap={16} />
                <YAxis fontSize={11} tickLine={false} axisLine={false} stroke="hsl(var(--muted-foreground))" />
                <Tooltip contentStyle={TOOLTIP_STYLE} cursor={BAR_CURSOR} />
                <Legend wrapperStyle={LEGEND_STYLE} />
                {units.map((u, i) => (
                  <Bar key={u} dataKey={u} name={u} stackId="sales" fill={CHART_COLORS[i % CHART_COLORS.length]} radius={i === units.length - 1 ? [6, 6, 0, 0] : [0, 0, 0, 0]} />
                ))}
              </BarChart>
            </ResponsiveContainer>
          )}
        </CardContent>
      </Card>

      {/* Per-product breakdown */}
      <Card className="mt-4">
        <CardHeader><CardTitle className="text-base">{t("dashboard.byProduct")}</CardTitle></CardHeader>
        <CardContent>
          <div className="overflow-x-auto thin-scroll">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{t("common.product")}</TableHead>
                  <TableHead>{t("common.unit")}</TableHead>
                  <TableHead className="text-right">{t("dashboard.current")}</TableHead>
                  <TableHead className="text-right">{t("dashboard.previous")}</TableHead>
                  <TableHead className="text-right">{t("dashboard.change")}</TableHead>
                  <TableHead className="text-right">{t("reports.revenue")}</TableHead>
                  <TableHead className="text-right">{t("reports.prevRevenue")}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {!hasData ? (
                  <TableRow><TableCell colSpan={7} className="py-8 text-center text-sm text-muted-foreground">{t("dashboard.noSales")}</TableCell></TableRow>
                ) : (data.by_product || []).map((r) => (
                  <TableRow key={`${r.product_type}-${r.unit}`} data-testid={`report-row-${r.product_type}-${r.unit}`}>
                    <TableCell className="font-medium">{td(r.product_type)}</TableCell>
                    <TableCell><Badge variant="outline" className="font-mono text-[10px]">{r.unit}</Badge></TableCell>
                    <TableCell className="text-right font-mono tabular-nums">{fmt(r.current, 2)}</TableCell>
                    <TableCell className="text-right font-mono tabular-nums text-muted-foreground">{fmt(r.previous, 2)}</TableCell>
                    <TableCell className="text-right"><Delta pct={r.change_pct} current={r.current} testid={`report-delta-${r.product_type}-${r.unit}`} /></TableCell>
                    <TableCell className="text-right font-mono tabular-nums">{fmt(r.current_revenue, 2)} {currency}</TableCell>
                    <TableCell className="text-right font-mono tabular-nums text-muted-foreground">{fmt(r.previous_revenue, 2)} {currency}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>
    </Layout>
  );
}
